/*
  File: src/components/Stream/StartStreamModal.jsx
  Purpose: Modal shown while starting a stream that blocks interactions.
*/
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

const StartStreamModal = ({
  isVisible,
  isProcessing,
  streamType = 'match'
}) => {
  // Block navigation and interactions during starting
  useEffect(() => {
    if (isVisible && isProcessing) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = 'Stream is starting. Please wait...';
        return 'Stream is starting. Please wait...';
      };
      const handlePopState = () => {
        if (isProcessing) {
          window.history.pushState(null, '', window.location.href);
        }
      };
      const handleClick = (e) => { e.preventDefault(); e.stopPropagation(); return false; };
      const handleKeyDown = (e) => {
        // block most keys
        if (e.key) { e.preventDefault(); e.stopPropagation(); return false; }
      };

      document.body.style.overflow = 'hidden';
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      window.history.pushState(null, '', window.location.href);
      document.addEventListener('click', handleClick, true);
      document.addEventListener('keydown', handleKeyDown, true);

      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handlePopState);
        document.removeEventListener('click', handleClick, true);
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }
  }, [isVisible, isProcessing]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative bg-white rounded-xl shadow-2xl p-8 mx-4 max-w-md w-full text-center"
      >
        <div className="mb-6">
          <div className="w-16 h-16 mx-auto mb-4 relative">
            <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-emerald-500 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Starting Stream
          </h3>
          <p className="text-gray-600">
            Please wait while we start your {streamType === 'series' ? 'series' : 'match'} stream.
            <br />
            <span className="text-sm text-gray-500 mt-2 block">
              This can take a few seconds...
            </span>
          </p>
        </div>
        <div className="mb-4">
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
            <div className="flex space-x-1">
              <motion.div
                className="w-2 h-2 bg-emerald-500 rounded-full"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: 0 }}
              />
              <motion.div
                className="w-2 h-2 bg-emerald-500 rounded-full"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: 0.2 }}
              />
              <motion.div
                className="w-2 h-2 bg-emerald-500 rounded-full"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }}
              />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default StartStreamModal;

